import {
  AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy, signal
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { BadgeColorTypeEnum } from 'app/back-office/shared/enum/badge-color-type.enum.model';
import { BadgeColorEnum } from 'app/back-office/shared/enum/badge-color.enum.model';
import { BadgeTypeEnum } from 'app/back-office/shared/enum/badge-type.enum.model';
import { SizeGroupTypeEnum } from 'app/back-office/shared/enum/size-group-type.enum.model';
import { SearchSettings } from 'app/back-office/shared/models/common/search-settings.model';
import { OrderService } from 'app/back-office/shared/services/back-office/order.service';
import { UtilsService } from 'app/back-office/shared/services/core/utils.service';
import { CustomDateVM, CustomDeliveriesToInsert, CustomDeliveriesToUpdate, CustomDeliveryDateDTI, CustomDeliveryDateVM, OrderItemFormVM } from 'app/shared/models/custom-deliveries.models';
import { ShopContextStore } from 'app/shared/state/shop-context.store';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { CustomQtyDateOrderItem } from '../order-items-custom-qty-date/order-items-custom-qty-date.component';

export interface ByDateGroup {
  date: string;
  type: 'original' | 'custom';
  itemIndexes: number[];
  totalQty: number;
  minRequestedDeliveryDate?: string;
  maxRequestedDeliveryDate?: string;
}

export interface CustomDateGroup {
  customDate: string;
  itemIndexes: number[];
  totalQtyForDate: number;
  minRequestedDeliveryDate: string;
  maxRequestedDeliveryDate: string;
  nOrderItems: number;
}

export interface StyleCodeGroup {
  productCode: string;
  productTitle: string;
  img: string;
  itemIndexes: number[];
  totalQty: number;
  minRequestedDeliveryDate: string;
  maxRequestedDeliveryDate: string;
  minCustomDate: string | null;
  maxCustomDate: string | null;
}

/**
 * Struttura locale usata per popolare il FormGroup di ogni order item all'interno del FormArray `orderItemsFormCustomDate`.
 * Non esposta verso l'esterno — è usata solo internamente per il reactive form.
 */
class OrderItemForm {
  orderItemId: number;
  isChecked: boolean;
  requestedDeliveryDate: string;
  img: string;
  size: string;
  color: string;
  qty: number;
  invalidCustomization: boolean;
  customDates: CustomDateForm[];
}

/**
 * Struttura locale per ogni riga di delivery date nel FormArray `customDates` di un order item.
 * Non esposta verso l'esterno.
 */
class CustomDateForm {
  date: string;
  qty: number;
}

export enum ViewMode {
  All = 1,
  originalDate = 2,
  customDate = 3,
  styleCode = 4
}

/**
 * `NewOrderItemCustomQtyDateComponent` è il successore di `OrderItemsCustomQtyDateComponent`.
 *
 * Permette all'utente di gestire le date di consegna personalizzate (con relative quantità) per ogni
 * order item di un pre-order, prima della conferma dell'ordine.
 *
 * ## Flusso di utilizzo
 * 1. Il parent chiama `openModal.next()` per aprire la modale
 * 2. Il componente legge le shop settings (`maxCustomDeliveryDate`, `supportsSplitDelivery`) da `ShopContextStore`
 * 3. L'utente aggiunge/rimuove/modifica le date per item o in bulk
 * 4. Al salvataggio il componente chiama l'API (se `orderId` è presente) ed emette `customDateSaved`
 * 5. Se l'utente annulla, emette `actionCanceled`
 *
 * ## Modalità `supportsSplitDelivery`
 * - `true`  → UI accordion, N date per item (split delivery)
 * - `false` → UI card, 1 sola data per item (il bulk-add svuota le date precedenti)
 *
 * ## Modalità senza `orderId` (es. shop/cart pre-save)
 * - La chiamata API viene saltata; il componente emette solo `customDateSaved` con i dati
 *   e il parent è responsabile del salvataggio.
 *
 * @selector app-new-order-item-custom-qty-date
 */
@Component({
    selector: 'app-new-order-item-custom-qty-date',
    templateUrl: './new-order-item-custom-qty-date.component.html',
    styleUrls: ['./new-order-item-custom-qty-date.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class NewOrderItemCustomQtyDateComponent implements OnChanges, AfterViewInit, OnDestroy {

  /** Riferimento al template `#ModalCustomDate` per l'apertura tramite NgbModal */
  @ViewChild('ModalCustomDate') modalCustomDate: any;

  ViewMode = ViewMode;
  viewMode: ViewMode = ViewMode.All;

  // ─── Inputs ───────────────────────────────────────────────────────────────

  /** Subject emesso dal parent per aprire la modale (trigger). Si sottoscrive in `ngAfterViewInit`. */
  @Input() openModal: Subject<void>;

  /** Se `true`, la modale mostra un bottone "Indietro" per tornare alla schermata precedente */
  @Input() showBackButton: boolean;

  /** Se `true`, la modale mostra la colonna "Stock Qty" per ogni item */
  @Input() showStockQty: boolean;

  /** Lista degli order item da gestire. Viene ordinata in `ngOnChanges` (productCode → colorCode → sizeOrder) */
  @Input() orderItems: CustomQtyDateOrderItem[];

  /**
   * ID dell'ordine da aggiornare tramite API al salvataggio.
   * Se `null` / non fornito, il componente emette solo l'evento senza chiamare l'API (modalità shop/no-save).
   */
  @Input() orderId: number;

  // ─── Outputs ──────────────────────────────────────────────────────────────

  /** Emesso al salvataggio con il payload delle delivery date modificate */
  @Output() customDateSaved = new EventEmitter<CustomDeliveryDateVM[]>();

  /** Emesso se il salvataggio non va a buon fine: il server può averne applicato una parte, il parent ricarica l'ordine */
  @Output() customDateSaveFailed = new EventEmitter<void>();

  /** Emesso quando l'utente annulla/chiude la modale senza salvare */
  @Output() actionCanceled = new EventEmitter<void>();

  // ─── Stato UI ─────────────────────────────────────────────────────────────

  /** Traccia se l'accordion è espanso — usato dal template per alternare Expand/Collapse All */
  accordionIsExpanded = false;

  /** Emette comandi expand/collapse all'accordion figlio tramite Subject (funziona anche dentro NgbModal portal) */
  accordionToggle$ = new Subject<'expand' | 'collapse'>();

  /** Espone i tipi di badge al template */
  badgeType = BadgeTypeEnum;
  badgeColorType = BadgeColorTypeEnum;
  badgeColorEnum = BadgeColorEnum;

  // ─── Dati form ────────────────────────────────────────────────────────────

  /**
   * Snapshot degli `orderItems` ricevuti in input (prima dell'eventuale filtro search).
   * Usato per ripristinare la lista completa quando il testo di ricerca viene cancellato.
   */
  cachedOrderItems: CustomQtyDateOrderItem[];

  /**
   * FormArray principale: ogni elemento è un `FormGroup` che rappresenta un order item
   * con le sue delivery date (`customDates: FormArray`) e i metadati (qty, size, colore, ecc.).
   */
  orderItemsFormCustomDate: FormArray = new FormArray([]);

  /**
   * Snapshot del valore iniziale del `FormArray` al momento dell'apertura della modale.
   * Usato per determinare le operazioni di insert/update/delete al salvataggio
   * (confronto tra stato attuale e stato iniziale).
   */
  cacheOrderItemsFormCustomDate: OrderItemFormVM[] = [];

  /** FormGroup contenitore usato dal template come wrapper del FormArray */
  orderItemCustomDate: FormGroup = new FormGroup({});

  // ─── Identificatori ───────────────────────────────────────────────────────

  /** ID univoco del componente (usato per evitare conflitti tra istanze multiple) */
  componentId = UtilityFunctions.newGuid();

  /** ID loader passato alle API call per mostrare/nascondere lo spinner */
  loaderId = UtilityFunctions.newGuid();

  // ─── Selezione bulk ───────────────────────────────────────────────────────

  /**
   * Lista degli `orderItemId` degli item attualmente selezionati (checkbox `isChecked`).
   * Aggiornata dalla subscription in `subscribeIsChecked`.
   */
  itemsSelected: number[] = [];

  /** `true` se tutti gli item del form sono selezionati */
  isSelectedAll: boolean = false;

  // ─── Date bulk ────────────────────────────────────────────────────────────

  /**
   * Data minima selezionabile nel date-picker bulk.
   * Calcolata come la `requestedDeliveryDate` più recente tra gli item selezionati.
   */
  minBulkDate: string;

  // ─── Shop settings ────────────────────────────────────────────────────────

  /**
   * Data massima oltre la quale le delivery date non sono ammesse.
   * Letta da `ShopContextStore.currentShop().maxCustomDeliveryDate` all'apertura della modale.
   */
  maxStoreCustomDate: string;

  /**
   * Indica se lo shop supporta la suddivisione di un item su più date di consegna.
   * - `true`  → UI accordion, N date per item
   * - `false` → UI card, 1 sola data per item
   * Letta da `ShopContextStore.currentShop().supportsSplitDelivery` all'apertura della modale.
   */
  supportsSplitDelivery: boolean;

  // ─── Flag visivi ──────────────────────────────────────────────────────────

  /**
   * `true` se almeno un item ha delivery date **già esistenti** che superano `maxStoreCustomDate`.
   * Viene impostato all'apertura della modale da `setIsDateOverMaxDate()`.
   * Usato nel template per mostrare un warning informativo (non blocca il salvataggio).
   */
  isDateOverMaxDate: boolean = false;

  /**
   * `true` se almeno uno degli item **selezionati** ha `requestedDeliveryDate` oltre `maxStoreCustomDate`.
   * Aggiornato dalla subscription in `subscribeIsChecked` al cambio della selezione.
   * Usato nel template per disabilitare il date-picker bulk quando non avrebbe senso.
   */
  selectedRequestedDeliveryDatesAreOverMaxDate: boolean = false;

  /**
   * `true` se il form è stato modificato rispetto allo stato iniziale (cache).
   * Il bottone "Save" rimane disabilitato finché `isChanged = false`.
   * Aggiornato dalla subscription in `subscribeCheckIfNotChanged`.
   */
  isChanged: boolean = false;

  /** `true` mentre il salvataggio è in corso: disabilita "Applica", così un secondo clic non rimanda le stesse cancellazioni */
  isSaving = signal(false);

  // ─── Search ───────────────────────────────────────────────────────────────

  /** Configurazione della search bar (placeholder, inputId per a11y) */
  searchSettings: SearchSettings = {
    placeholder: 'Search...',
    inputId: UtilityFunctions.newGuid()
  };

  // ─── Subscription ─────────────────────────────────────────────────────────

  /** Raccoglie tutte le subscription attive — vengono annullate in `ngOnDestroy` */
  private subscription = new Subscription();

  private _shopContext = inject(ShopContextStore);

  constructor(
    private _ngbModalService: NgbModal,
    private _orderService: OrderService,
    private _utils: UtilsService,
    private _translate: TranslateService,
  ) { }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  /**
   * Reagisce ai cambiamenti dell'input `orderItems`:
   * - Ordina gli item per `productCode` → `colorCode` → `sizeOrder`
   * - Salva uno snapshot nella `cachedOrderItems` (usato dalla search per filtrare)
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderItems) {
      this.orderItems = this.orderItems?.sort((v, v2) => {
        if (v.productCode < v2.productCode) { return -1; }
        if (v.productCode > v2.productCode) { return 1; }
        if (v.colorCode < v2.colorCode) { return -1; }
        if (v.colorCode > v2.colorCode) { return 1; }
        if (v.sizeOrder < v2.sizeOrder) { return -1; }
        if (v.sizeOrder > v2.sizeOrder) { return 1; }
        return 0;
      });
      this.cachedOrderItems = UtilityFunctions.DeepClone(this.orderItems);
    }
  }

  /**
   * Dopo il rendering iniziale, si sottoscrive al Subject `openModal` (ricevuto in input dal parent).
   * Ogni volta che il Subject emette:
   * 1. Legge le shop settings dallo shop corrente
   * 2. Inizializza il FormArray con gli order item
   * 3. Apre la modale NgbModal
   * 4. Avvia le subscription interne (selezione e rilevamento modifiche)
   * 5. Imposta il flag `isDateOverMaxDate`
   */
  ngAfterViewInit(): void {
    this.subscription.add(this.openModal.subscribe(() => {
      this.maxStoreCustomDate = this._shopContext.currentShop().maxCustomDeliveryDate;
      this.supportsSplitDelivery = this._shopContext.currentShop().supportsSplitDelivery;
      this.initOrderItemsFormCustomDate();
      this.onOpenModal();
      this.subscribeIsChecked();
      this.subscribeCheckIfNotChanged();
      this.setIsDateOverMaxDate();
    }));
  }

  /** Annulla tutte le subscription attive quando il componente viene distrutto */
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ─── Metodi pubblici (UI) ─────────────────────────────────────────────────

  /**
   * Filtra gli order item in base al testo di ricerca (case-insensitive).
   * La ricerca avviene su `variantCode` e `productTitle`.
   * Dopo il filtro reinizializza il FormArray per rispecchiare la lista filtrata.
   *
   * @param searchText testo inserito nella search bar
   */
  onSearch(searchText: string) {
    this.orderItems = this.cachedOrderItems?.filter(oi => oi.variantCode?.toLowerCase().indexOf(searchText?.toLowerCase()) >= 0 || oi.productTitle?.toLowerCase().indexOf(searchText?.toLowerCase()) >= 0);
    this.initOrderItemsFormCustomDate();
  }

  /**
   * Aggiunge la data di disponibilità a stock a tutti gli item **selezionati** che hanno `stockQty > 0`.
   * - Se `supportsSplitDelivery = false`, svuota prima le date esistenti dell'item
   * - Per gli item selezionati ma senza stock, incrementa un contatore e mostra uno snackbar warning
   */
  onBulkCustomStockDate(): void {
    let indexCountWithNoStockQty = 0;
    this.orderItemsFormCustomDate.controls.forEach((orderItemForm: FormGroup, orderItemIndex: number) => {
      if (orderItemForm.get('isChecked').value) {
        if (orderItemForm.get('stockQty').value) {
          if (!this.supportsSplitDelivery) (orderItemForm.get('customDates') as FormArray).clear();
          this.addCustomStockDate(orderItemIndex);
          (orderItemForm.get('customDates') as FormArray).markAllAsTouched();
          orderItemForm.markAsTouched();
        } else {
          indexCountWithNoStockQty++;
        }
      }
    });
    if (indexCountWithNoStockQty > 0) {
      this._utils.showSnackbar(this._translate.instant('CUSTOM_SHIP_DATES.ITEMS_WITH_NO_STOCK1') + indexCountWithNoStockQty + this._translate.instant('CUSTOM_SHIP_DATES.ITEMS_WITH_NO_STOCK2'), 5000, 'warning');
    }
  }

  /**
   * Aggiunge la data `date` a tutti gli item **selezionati**.
   * - Se `supportsSplitDelivery = false`, svuota prima le date esistenti dell'item (una sola data ammessa)
   * - Usa `addCustomDate` per ogni item, che gestisce la redistribuzione delle quantità
   *
   * @param date data selezionata nel date-picker bulk (stringa ISO)
   */
  onBulkAddCustomDate(date: string): void {
    this.orderItemsFormCustomDate.controls.forEach((orderItemForm: FormGroup, orderItemIndex: number) => {
      if (orderItemForm.get('isChecked').value) {
        (orderItemForm.get('customDates') as FormArray).clear();
        this.addCustomDate(orderItemIndex, date);
        (orderItemForm.get('customDates') as FormArray).markAllAsTouched();
        orderItemForm.markAsTouched();
      }
    });
  }

  /**
   * Rimuove tutte le delivery date da tutti gli item **selezionati**.
   * Il FormArray `customDates` di ogni item selezionato viene svuotato completamente.
   */
  onBulkRemoveCustomDate(): void {
    this.orderItemsFormCustomDate.controls.forEach((orderItemForm: FormGroup) => {
      if (orderItemForm.get('isChecked').value)
        (orderItemForm.get('customDates') as FormArray).clear();
    });
  }

  /**
   * Aggiunge una nuova riga di delivery date al FormArray `customDates` di un singolo item.
   *
   * La quantità viene **redistribuita equamente** tra tutte le righe (esistenti + nuova):
   * - `baseQty = floor(totalQty / totalRows)`
   * - Le prime `remainder` righe ricevono `baseQty + 1` (distribuzione del resto)
   * - Le righe esistenti vengono aggiornate, la nuova riga viene aggiunta con la quantità finale
   *
   * @param orderItemIndex indice dell'item nel FormArray `orderItemsFormCustomDate`
   * @param customDate     data da preimpostare sulla nuova riga (default: `requestedDeliveryDate` dell'item)
   */
  addCustomDate(orderItemIndex: number, customDate?: string): void {
    const orderItemForm = this.orderItemsFormCustomDate.controls[orderItemIndex] as FormGroup;
    const customDates = orderItemForm.get('customDates') as FormArray;
    const requestedDeliveryDate = orderItemForm.get('requestedDeliveryDate').value as string;

    if (customDate && this.isSameCalendarDate(customDate, requestedDeliveryDate)) {
      return;
    }

    if (!this.supportsSplitDelivery) {
      if (customDate) {
        customDates.clear();
        customDates.push(this.initCustomDate(
          customDate,
          orderItemForm.get('qty').value,
          orderItemForm.get('qty').value,
          requestedDeliveryDate,
          null,
          orderItemForm.get('stockQty').value
        ));
      } else if (!customDates.length) {
        customDates.push(this.initCustomDate(
          this.getNextCalendarDate(requestedDeliveryDate),
          orderItemForm.get('qty').value,
          orderItemForm.get('qty').value,
          requestedDeliveryDate,
          null,
          orderItemForm.get('stockQty').value
        ));
      }
      return;
    }

    const totalQty = orderItemForm.get('qty').value;
    const totalRows = customDates.length + 1;

    // Divisione equa: floor a tutti e distribuzione dei rimanenti
    const baseQty = Math.floor(totalQty / totalRows);
    const remainder = totalQty % totalRows;

    // Nuovo array con quantità distribuite
    const distributedQtys = Array.from({ length: totalRows }, (_, i) =>
      i < remainder ? baseQty + 1 : baseQty
    );

    // Aggiorna le righe esistenti con la nuova distribuzione
    customDates.controls.forEach((ctrl, index) => {
      ctrl.get('qty').setValue(distributedQtys[index]);
    });

    // Aggiungi nuova riga con la quantità finale calcolata
    const newQty = distributedQtys[distributedQtys.length - 1];
    customDates.push(this.initCustomDate(
      customDate ? customDate : this.getNextCalendarDate(requestedDeliveryDate),
      newQty,
      totalQty,
      requestedDeliveryDate,
      null,
      orderItemForm.get('stockQty').value
    ));
  }

  /**
   * Aggiunge una riga di delivery date con la data di disponibilità a stock (`computedDeliveryDateDaysDelta`)
   * e la quantità `min(orderedQty, stockQty)`.
   * Non aggiunge nulla se `stockQty` è 0.
   *
   * @param orderItemIndex indice dell'item nel FormArray `orderItemsFormCustomDate`
   */
  addCustomStockDate(orderItemIndex: number) {
    const orderItemForm = this.orderItemsFormCustomDate.controls[orderItemIndex] as FormGroup;
    const customDates = orderItemForm.get('customDates') as FormArray;

    let stockQty = orderItemForm.get('qty').value < orderItemForm.get('stockQty').value ? orderItemForm.get('qty').value : orderItemForm.get('stockQty').value;
    if (stockQty > 0) {
      if (!this.supportsSplitDelivery) {
        customDates.clear();
      }
      customDates.push(this.initCustomDate(
        UtilityFunctions.getReOrderAvailableDate(this._shopContext.currentShop().computedDeliveryDateDaysDelta),
        stockQty,
        orderItemForm.get('qty').value,
        orderItemForm.get('requestedDeliveryDate').value,
        null,
        orderItemForm.get('stockQty').value
      ));
    }
  }

  /**
   * Rimuove una singola riga di delivery date da un item specifico.
   *
   * @param orderItemIndex   indice dell'item nel FormArray `orderItemsFormCustomDate`
   * @param customDateIndex  indice della riga da rimuovere nel FormArray `customDates` dell'item
   */
  removeCustomDate(orderItemIndex: number, customDateIndex: number): void {
    const orderItemForm = this.orderItemsFormCustomDate.controls[orderItemIndex] as FormGroup;
    const customDates = orderItemForm.get('customDates') as FormArray;
    customDates.removeAt(customDateIndex);
  }

  onClearItemCustomDates(orderItemIndex: number): void {
    const orderItemForm = this.orderItemsFormCustomDate.controls[orderItemIndex] as FormGroup;
    const customDates = orderItemForm.get('customDates') as FormArray;
    customDates.clear();
    customDates.markAsTouched();
    orderItemForm.markAsTouched();
  }

  /** Seleziona tutti gli item del form (imposta `isChecked = true` su ogni FormGroup) */
  onSelectAll(): void {
    this.orderItemsFormCustomDate.controls.forEach((orderItemForm: FormGroup) => {
      orderItemForm.get('isChecked').setValue(true);
    });
  }

  /** Deseleziona tutti gli item del form (imposta `isChecked = false` su ogni FormGroup) */
  onUnselectAll(): void {
    this.orderItemsFormCustomDate.controls.forEach((orderItemForm: FormGroup) => {
      orderItemForm.get('isChecked').setValue(false);
    });
  }

  /**
   * Callback del custom date-time-picker: aggiorna il valore del FormControl `date`
   * e riesegue i validatori sulla riga.
   *
   * @param ev   data emessa dal picker (stringa ISO)
   * @param form FormControl `date` della riga da aggiornare
   */
  onDateTimeEmit(ev: string, form: AbstractControl) {
    form.setValue(ev);
    form.updateValueAndValidity();
  }

  /**
   * Cambia la modalità di visualizzazione.
   *
   * @param viewMode la modalità di visualizzazione (ViewMode)
   */
  onChangeViewMode(viewMode: ViewMode) {
    if (viewMode === ViewMode.customDate && !this.hasCustomDateItems) return;
    this.onUnselectAll();
    this.viewMode = viewMode;
  }

  get hasOriginalDateItems(): boolean {
    return this.orderItemsFormCustomDate.controls.length > 0;
  }

  get hasCustomDateItems(): boolean {
    return this.customDateGroups.length > 0;
  }

  onUnselectItem(index: number): void {
    const ctrl = this.orderItemsFormCustomDate.controls[index] as FormGroup;
    ctrl.get('isChecked').setValue(false);
  }

  get originalDateGroups(): ByDateGroup[] {
    const originalMap = new Map<string, ByDateGroup>();
    const customMap = new Map<string, ByDateGroup>();

    this.orderItemsFormCustomDate.controls.forEach((ctrl, index) => {
      const requestedDeliveryDate: string = ctrl.get('requestedDeliveryDate').value;
      const customDates = ((ctrl.get('customDates').value as CustomDateVM[]) ?? [])
        .filter(cd => cd.date && !this.isSameCalendarDate(cd.date, requestedDeliveryDate));

      if (customDates.length === 0) {
        if (!originalMap.has(requestedDeliveryDate)) {
          originalMap.set(requestedDeliveryDate, {
            date: requestedDeliveryDate, type: 'original', itemIndexes: [], totalQty: 0,
            minRequestedDeliveryDate: requestedDeliveryDate, maxRequestedDeliveryDate: requestedDeliveryDate,
          });
        }
        const group = originalMap.get(requestedDeliveryDate);
        group.itemIndexes.push(index);
        group.totalQty += ctrl.get('qty').value as number;
      } else {
        customDates.forEach(cd => {
          const dateKey = cd.date?.split('T')[0];
          if (!customMap.has(dateKey)) {
            customMap.set(dateKey, {
              date: cd.date, type: 'custom', itemIndexes: [], totalQty: 0,
              minRequestedDeliveryDate: requestedDeliveryDate,
              maxRequestedDeliveryDate: requestedDeliveryDate,
            });
          }
          const group = customMap.get(dateKey);
          if (!group.itemIndexes.includes(index)) { group.itemIndexes.push(index); }
          group.totalQty += cd.qty as number;
          if (requestedDeliveryDate < group.minRequestedDeliveryDate) { group.minRequestedDeliveryDate = requestedDeliveryDate; }
          if (requestedDeliveryDate > group.maxRequestedDeliveryDate) { group.maxRequestedDeliveryDate = requestedDeliveryDate; }
        });
      }
    });

    return [...originalMap.values(), ...customMap.values()]
      .sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : 0);
  }

  get customDateGroups(): CustomDateGroup[] {
    const map = new Map<string, CustomDateGroup>();
    this.orderItemsFormCustomDate.controls.forEach((ctrl, index) => {
      const requestedDeliveryDate: string = ctrl.get('requestedDeliveryDate').value;
      const customDates = ((ctrl.get('customDates').value as CustomDateVM[]) ?? [])
        .filter(cd => cd.date && !this.isSameCalendarDate(cd.date, requestedDeliveryDate));
      if (!customDates.length) { return; }
      customDates.forEach(cd => {
        const dateKey: string = cd.date?.split('T')[0];
        if (!map.has(dateKey)) {
          map.set(dateKey, {
            customDate: cd.date,
            itemIndexes: [],
            totalQtyForDate: 0,
            minRequestedDeliveryDate: requestedDeliveryDate,
            maxRequestedDeliveryDate: requestedDeliveryDate,
            nOrderItems: 0,
          });
        }
        const group = map.get(dateKey);
        if (!group.itemIndexes.includes(index)) {
          group.itemIndexes.push(index);
          group.nOrderItems++;
        }
        group.totalQtyForDate += cd.qty as number;
        if (requestedDeliveryDate < group.minRequestedDeliveryDate) { group.minRequestedDeliveryDate = requestedDeliveryDate; }
        if (requestedDeliveryDate > group.maxRequestedDeliveryDate) { group.maxRequestedDeliveryDate = requestedDeliveryDate; }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.customDate < b.customDate ? -1 : a.customDate > b.customDate ? 1 : 0);
  }

  get styleCodeGroups(): StyleCodeGroup[] {
    const map = new Map<string, StyleCodeGroup>();
    this.orderItemsFormCustomDate.controls.forEach((ctrl, index) => {
      const productCode: string = ctrl.get('productCode').value;
      const requestedDeliveryDate: string = ctrl.get('requestedDeliveryDate').value;
      const customDates = ((ctrl.get('customDates').value as CustomDateVM[]) ?? [])
        .filter(cd => !this.isSameCalendarDate(cd.date, requestedDeliveryDate));
      if (!map.has(productCode)) {
        map.set(productCode, {
          productCode,
          productTitle: ctrl.get('productTitle').value,
          img: ctrl.get('img').value,
          itemIndexes: [],
          totalQty: 0,
          minRequestedDeliveryDate: requestedDeliveryDate,
          maxRequestedDeliveryDate: requestedDeliveryDate,
          minCustomDate: null,
          maxCustomDate: null,
        });
      }
      const group = map.get(productCode);
      group.itemIndexes.push(index);
      group.totalQty += ctrl.get('qty').value as number;
      if (requestedDeliveryDate < group.minRequestedDeliveryDate) { group.minRequestedDeliveryDate = requestedDeliveryDate; }
      if (requestedDeliveryDate > group.maxRequestedDeliveryDate) { group.maxRequestedDeliveryDate = requestedDeliveryDate; }
      customDates.forEach(cd => {
        const dateStr: string = cd.date;
        if (!group.minCustomDate || dateStr < group.minCustomDate) { group.minCustomDate = dateStr; }
        if (!group.maxCustomDate || dateStr > group.maxCustomDate) { group.maxCustomDate = dateStr; }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.productCode < b.productCode ? -1 : a.productCode > b.productCode ? 1 : 0);
  }

  onExpandCollapseAll() {
    this.accordionIsExpanded = !this.accordionIsExpanded;
    this.accordionToggle$.next(this.accordionIsExpanded ? 'expand' : 'collapse');
  }

  /**
   * Salva le delivery date modificate.
   *
   * ## Algoritmo di diff
   * Per ogni item nel form, confronta lo stato attuale con la cache iniziale (`cacheOrderItemsFormCustomDate`):
   * - Righe **con ID** già presente in cache → `deliveriesToUpdate`
   * - Righe **senza ID** (nuove) → `deliveriesToInsert`
   * - ID presenti in cache ma non più nel form → `deliveryIdsToDelete`
   *
   * Vengono inclusi nel payload solo gli item che hanno almeno una modifica.
   *
   * ## Chiamata API
   * - Se `orderId` è presente: chiama `OrderService.customDeliveryDate()` (forkJoin, max 50 parallele)
   *   e poi emette `customDateSaved`
   * - Se `orderId` è assente (modalità shop/no-save): emette `customDateSaved` direttamente
   *   senza chiamata API
   * - Se il salvataggio non va a buon fine: emette `customDateSaveFailed` (il parent ricarica l'ordine)
   *
   * In tutti i casi la modale viene chiusa con `dismissAll`.
   */
  onSaveCustomDates(): void {
    const orderItemsForm: OrderItemFormVM[] = this.orderItemsFormCustomDate.value;
    const requestedDeliveryDateByVariantId = new Map<number, string>(
      this.orderItemsFormCustomDate.controls.map(ctrl => {
        const formGroup = ctrl as FormGroup;
        return [
          formGroup.get('variantId').value as number,
          formGroup.get('requestedDeliveryDate').value as string,
        ];
      })
    );
    let customDeliveryDates: CustomDeliveryDateVM[] = [];
    orderItemsForm?.forEach(orderItemForm => {
      const requestedDeliveryDate = requestedDeliveryDateByVariantId.get(orderItemForm.variantId);
      const filteredCustomDates = orderItemForm.customDates.filter(customDate => !this.isSameCalendarDate(customDate.date, requestedDeliveryDate));
      const customDatesWithId: CustomDateVM[] = filteredCustomDates.filter(customDate => customDate.id);
      const customDatesWithoutId: CustomDateVM[] = filteredCustomDates.filter(customDate => !customDate.id);
      const cacheCustomDate = this.cacheOrderItemsFormCustomDate
        .find((cacheOrderItemForm: OrderItemFormVM) => cacheOrderItemForm.variantId === orderItemForm.variantId)
        .customDates
        .filter(customDate => !this.isSameCalendarDate(customDate.date, requestedDeliveryDate));

      const customDeliveriesToInsert: CustomDeliveriesToInsert[] = customDatesWithoutId.map(customDate => {
        return new CustomDeliveriesToInsert({
          customDeliveryDate: customDate.date,
          quantity: customDate.qty,
        });
      });

      const customDeliveriesToUpdate: CustomDeliveriesToUpdate[] = customDatesWithId.filter(customDate =>
        cacheCustomDate.find(cacheCustomDate => cacheCustomDate.id === customDate.id))
        .map(customDate => {
          return new CustomDeliveriesToUpdate({
            id: customDate.id,
            customDeliveryDate: customDate.date,
            quantity: customDate.qty,
          });
        });

      const customDeliveryIdsToDelete: number[] = cacheCustomDate.filter(cacheCustomDate => !customDatesWithId.find(customDate => customDate.id === cacheCustomDate.id)).map(cacheCustomDate => cacheCustomDate.id);

      if (customDeliveriesToInsert.length || customDeliveriesToUpdate.length || customDeliveryIdsToDelete.length) {
        customDeliveryDates.push(new CustomDeliveryDateVM({
          variantId: orderItemForm.variantId,
          orderItemId: orderItemForm.orderItemId,
          deliveries: new CustomDeliveryDateDTI({
            deliveriesToInsert: customDeliveriesToInsert || [],
            deliveriesToUpdate: customDeliveriesToUpdate || [],
            deliveryIdsToDelete: customDeliveryIdsToDelete || [],
          }),
        }));
      }
    });

    // Senza righe da inviare niente chiamata: il forkJoin con lista vuota non risponde mai
    if (!this.orderId || !customDeliveryDates.length) {
      this.customDateSaved.emit(customDeliveryDates);
      this._ngbModalService.dismissAll();
      return;
    }

    this.isSaving.set(true);
    let saved = false;
    this._orderService.customDeliveryDate(this.loaderId, customDeliveryDates, this.orderId).subscribe({
      next: () => {
        saved = true;
        this.isSaving.set(false);
        this.customDateSaved.emit(customDeliveryDates);
        this._ngbModalService.dismissAll();
      },
      error: () => this.onSaveFailed(),
      // Il forkJoin si chiude senza dati se anche una sola POST fallisce (le altre possono essere già salvate)
      complete: () => {
        if (!saved) this.onSaveFailed();
      }
    });
  }

  // ─── Metodi privati ───────────────────────────────────────────────────────

  /** La fotografia delle consegne non è più affidabile: si chiude e il parent ricarica l'ordine. */
  private onSaveFailed(): void {
    this.isSaving.set(false);
    this.customDateSaveFailed.emit();
    this._ngbModalService.dismissAll();
  }

  /**
   * Crea il FormGroup per una singola riga di delivery date (un elemento del FormArray `customDates`).
   *
   * Calcola `isStockDate` confrontando la data con la data di stock dello shop
   * (`computedDeliveryDateDaysDelta`): se corrisponde, il validator `max` usa `stockQty`
   * come limite massimo (invece di `orderedQty`).
   *
   * Validatori applicati:
   * - `date`: `required`, `currentDateOverMaxDate` (data non oltre `maxStoreCustomDate`)
   * - `qty`: `required`, `min(1)`, `max(stockQty | orderedQty)`
   *
   * @param date       data della delivery (stringa ISO)
   * @param qty        quantità per questa riga
   * @param orderedQty quantità totale ordinata dell'item (usata come max per qty se non stock date)
   * @param id         ID della delivery esistente (presente se già salvata sul server, null se nuova)
   * @param stockQty   quantità disponibile a stock (usata come max per qty se è stock date)
   */
  private initCustomDate(date: string, qty: number, orderedQty: number, requestedDeliveryDate: string, id?: number, stockQty?: number): FormGroup {
    const isStockDate = UtilityFunctions.fromDateStringTODateISOString(date) === UtilityFunctions.getReOrderAvailableDate(this._shopContext.currentShop().computedDeliveryDateDaysDelta);
    const result = new FormGroup({
      isStockDate: new FormControl(isStockDate),
      date: new FormControl(date, [Validators.required, this.currentDateOverMaxDate(), this.currentDateEqualsRequestedDate(requestedDeliveryDate)]),
      qty: new FormControl(qty, [Validators.required, Validators.min(1), Validators.max(isStockDate ? stockQty : orderedQty)]),
      id: new FormControl(id),
    });
    return result;
  }

  /**
   * Apre la modale NgbModal con il template `#ModalCustomDate`.
   *
   * Alla chiusura/dismiss:
   * - Se il motivo è `'actionCanceled'` (dismiss esplicito dal bottone Annulla), emette `actionCanceled`
   * - Negli altri casi (es. click outside, ESC) non emette nulla
   */
  private onOpenModal(): void {
    this._ngbModalService.open(this.modalCustomDate, {
      centered: true,
      backdrop: 'static',
      scrollable: false,
      windowClass: 'modal modal-full-width',
    }).result.then((result) => {
    }, (reason: 'actionCanceled' | null) => {
      if (reason === 'actionCanceled') this.actionCanceled.emit();
    });
  }

  /**
   * Inizializza (o reinizializza) il FormArray `orderItemsFormCustomDate`:
   * - Svuota il FormArray e lo marca come pristine
   * - Crea un FormGroup per ogni item in `orderItems` tramite `initOrderItemCustomDate`
   * - Salva uno snapshot del valore iniziale in `cacheOrderItemsFormCustomDate`
   *   (base per il diff al salvataggio e per `isChanged`)
   */
  private initOrderItemsFormCustomDate(): void {
    this.orderItemsFormCustomDate.clear();
    this.orderItemsFormCustomDate.markAsPristine();
    this.orderItems.forEach((orderItem: CustomQtyDateOrderItem) =>
      this.orderItemsFormCustomDate.push(this.initOrderItemCustomDate(orderItem)));
    this.cacheOrderItemsFormCustomDate = UtilityFunctions.DeepClone(this.orderItemsFormCustomDate.value);
    this.ensureAvailableViewMode();
  }

  /**
   * Crea il FormGroup per un singolo order item nel FormArray principale.
   *
   * Le delivery date già esistenti sull'item (`orderItem.deliveries`) vengono
   * convertite in FormGroup tramite `initCustomDate` e inserite nel FormArray `customDates`.
   *
   * Il FormArray `customDates` ha due validatori a livello di array:
   * - `quantityValidator`: la somma delle qty nelle righe deve essere uguale alla qty ordinata
   * - `multipleDateValidator`: non sono ammesse date duplicate nello stesso item
   *
   * @param orderItem l'order item da rappresentare nel form
   */
  private initOrderItemCustomDate(orderItem: CustomQtyDateOrderItem): FormGroup {
    const customDates = orderItem.deliveries?.length > 0
      ? orderItem.deliveries.map((customDelivery) => this.initCustomDate(customDelivery.customDeliveryDate, customDelivery.quantity, orderItem.quantity, orderItem.requestedDeliveryDate, customDelivery.id, orderItem.inStockQty))
      : [];
    const checked = !!this.itemsSelected?.some((id: number) => id === orderItem.orderItemId);
    return new FormGroup({
      isChecked: new FormControl(checked),
      requestedDeliveryDate: new FormControl(orderItem.requestedDeliveryDate),
      img: new FormControl(UtilityFunctions.fixImagePath(orderItem.variantMedia)),
      size: new FormControl(orderItem.sizeName),
      sizeGroup: new FormControl(SizeGroupTypeEnum[orderItem.sizeGroup]),
      color: new FormControl(orderItem.colorCode),
      colorName: new FormControl(orderItem.paletteDescription),
      qty: new FormControl(orderItem.quantity),
      stockQty: new FormControl(orderItem.inStockQty),
      productCode: new FormControl(orderItem.productCode),
      productTitle: new FormControl(orderItem.productTitle),
      variantId: new FormControl(orderItem.variantId),
      orderItemId: new FormControl(orderItem.orderItemId),
      invalidCustomization: new FormControl(true),
      customDates: new FormArray(customDates, [this.quantityValidator(), this.multipleDateValidator()]),
    });
  }

  /**
   * Validator a livello di FormArray `customDates`:
   * verifica che la somma delle quantità nelle righe sia uguale alla quantità ordinata dell'item.
   *
   * Errori restituiti:
   * - `maxQtyReached`    — somma > qty ordinata
   * - `minQtyNotReached` — somma < qty ordinata
   */
  private quantityValidator(): ValidatorFn {
    return (controls: FormArray): ValidationErrors | null => {
      let error = null;
      if (controls.value.length > 0) {
        const qty = controls.value.reduce((acc, curr) => acc + curr.qty, 0);
        if (qty > controls.parent?.get('qty').value) error = { 'maxQtyReached': 'CUSTOM_SHIP_DATES.ERROR_THE_SUM_OF_THE_QUANTITIES_FOR_CUSTOM_DATES_IS_GREATER_THAN_THE_QUANTITY_ORDERED' };
        if (qty < controls.parent?.get('qty').value) error = { 'minQtyNotReached': 'CUSTOM_SHIP_DATES.ERROR_THE_SUM_OF_THE_QUANTITIES_FOR_CUSTOM_DATES_IS_LESS_THAN_THE_QUANTITY_ORDERED' };
      }
      return error;
    };
  }

  /**
   * Validator a livello di FormArray `customDates`:
   * verifica che non ci siano date duplicate per lo stesso item (confronto sul solo giorno, senza orario).
   *
   * Errore restituito:
   * - `duplicateDate` — almeno due righe hanno la stessa data
   */
  private multipleDateValidator(): ValidatorFn {
    return (controls: FormArray): ValidationErrors | null => {
      const dates = controls.value.map((customDate) => customDate.date?.split('T')[0]);
      const isDuplicate = dates.some((date, index) => dates.indexOf(date) !== index);
      return isDuplicate
        ? { 'duplicateDate': 'CUSTOM_SHIP_DATES.ERROR_THERE_ARE_DUPLICATE_DATES' }
        : null;
    };
  }

  /**
   * Validator a livello di singolo FormControl `date`:
   * verifica che la data selezionata non superi `maxStoreCustomDate`.
   *
   * Se la data supera il limite:
   * - Imposta `invalidCustomization = false` sul FormGroup dell'item padre
   *   (via `setTimeout` per evitare ExpressionChangedAfterItHasBeenCheckedError)
   * - Restituisce l'errore `currentDateOverMaxDate`
   *
   * Se la data è valida, imposta `invalidCustomization = true` e restituisce `null`.
   *
   * Errore restituito:
   * - `currentDateOverMaxDate` — la data supera `maxStoreCustomDate`
   */
  private currentDateOverMaxDate(): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      const parentOrderItemForm = control.parent?.parent?.parent;
      const checkDateResult = UtilityFunctions.checkCustomDeliveryDates(this.maxStoreCustomDate, [control.value]);
      if (!checkDateResult) {
        setTimeout(() => parentOrderItemForm?.get('invalidCustomization').setValue(checkDateResult));
        return { 'currentDateOverMaxDate': 'CUSTOM_SHIP_DATES.ERROR_THE_CURRENT_DATE_IS_OVER_THE_MAX_DATE' };
      } else {
        parentOrderItemForm?.get('invalidCustomization').setValue(checkDateResult);
        return null;
      }
    };
  }

  private currentDateEqualsRequestedDate(requestedDeliveryDate: string): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
      return this.isSameCalendarDate(control.value, requestedDeliveryDate)
        ? { 'currentDateEqualsRequestedDate': 'CUSTOM_SHIP_DATES.DATE_NOT_SELECTABLE' }
        : null;
    };
  }

  /**
   * Si sottoscrive alle variazioni del FormArray per aggiornare i dati dipendenti dalla selezione.
   *
   * Ad ogni cambiamento (con debounce di 100ms), per gli item selezionati (`isChecked = true`):
   * - `minBulkDate`: data minima ammessa per il bulk date-picker
   *   (la `requestedDeliveryDate` più recente tra gli item selezionati)
   * - `selectedRequestedDeliveryDatesAreOverMaxDate`: `true` se almeno un selezionato ha
   *   `requestedDeliveryDate` oltre `maxStoreCustomDate`
   * - `itemsSelected`: lista degli `orderItemId` selezionati (persistita tra reinizializzazioni)
   * - `bulkDate`: resettato a `null` ad ogni cambio di selezione
   */
  private subscribeIsChecked() {
    this.subscription.add(this.orderItemsFormCustomDate.valueChanges.pipe(
      debounceTime(100),
      map(orderItems => orderItems.filter(orderItem => orderItem.isChecked)),
    ).subscribe((value: OrderItemForm[]) => {
      const minSelectedOriginalDate = UtilityFunctions.elaborateMinDateInCustomDeliveryDate(value.map((orderItemForm: OrderItemForm) => orderItemForm.requestedDeliveryDate));
      this.minBulkDate = minSelectedOriginalDate ? this.getNextCalendarDate(minSelectedOriginalDate) : '';
      this.selectedRequestedDeliveryDatesAreOverMaxDate = value.some((orderItemForm: OrderItemForm) => !UtilityFunctions.checkCustomDeliveryDates(this.maxStoreCustomDate, [orderItemForm.requestedDeliveryDate]));
      this.itemsSelected = value.map((orderItemForm: OrderItemForm) => orderItemForm.orderItemId);
      this.isSelectedAll = value.length > 0 && value.length === this.orderItemsFormCustomDate.controls.length;
    }));
  }

  /**
   * Si sottoscrive alle variazioni del FormArray per aggiornare il flag `isChanged`.
   *
   * Ad ogni cambiamento (con debounce di 100ms), confronta i `customDates` attuali con quelli
   * della cache iniziale (`cacheOrderItemsFormCustomDate`).
   * - `isChanged = true`  → il form è stato modificato → il bottone "Save" si abilita
   * - `isChanged = false` → nessuna modifica rispetto allo stato iniziale → Save disabilitato
   */
  private subscribeCheckIfNotChanged() {
    this.subscription.add(this.orderItemsFormCustomDate.valueChanges.pipe(
      debounceTime(100),
    ).subscribe((value: OrderItemFormVM[]) => {
      const currentCustomDates = value.map((orderItemForm: OrderItemFormVM) => orderItemForm.customDates);
      const cacheCustomDates = this.cacheOrderItemsFormCustomDate.map((orderItemForm: OrderItemFormVM) => orderItemForm.customDates);
      this.isChanged = !UtilityFunctions.isArraysEquals(currentCustomDates, cacheCustomDates);
      this.ensureAvailableViewMode();
    }));
  }

  /**
   * Imposta il flag `isDateOverMaxDate` all'apertura della modale.
   *
   * Controlla se almeno uno degli `orderItems` ha delivery date **già esistenti**
   * (ricevute in input) che superano `maxStoreCustomDate`.
   * Usato nel template per mostrare un warning informativo all'utente.
   */
  private setIsDateOverMaxDate() {
    this.isDateOverMaxDate = this.orderItems.some(x => !UtilityFunctions.checkCustomDeliveryDates(this.maxStoreCustomDate, (x.deliveries || []).map(cd => cd.customDeliveryDate)));
  }

  private isSameCalendarDate(firstDate: string, secondDate: string): boolean {
    return firstDate?.split('T')[0] === secondDate?.split('T')[0];
  }

  private getNextCalendarDate(date: string): string {
    if (!date) return date;
    const [yearStr, monthStr, dayStr] = date.split('T')[0].split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    const nextDateUtc = new Date(Date.UTC(year, month - 1, day + 1));
    return nextDateUtc.toISOString();
  }

  private ensureAvailableViewMode(): void {
    if (this.viewMode === ViewMode.customDate && !this.hasCustomDateItems) {
      this.viewMode = ViewMode.All;
    }
  }
}
